import { Link } from '@ksyos/router';
import { useEffect, useState } from 'react';
import { ManagerAuthorization } from '@common/enums';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import type { IAuthorization } from '../../interfaces/authorization';
import type { IService } from '../../interfaces/service';
import type { ISubsequentService } from '../../interfaces/subsequentService';
import { fetchJson } from '../../../store/fetch';
import { translateKey } from '../../../translate';
import {
    AdminTable,
    Button,
    DeleteButton,
    Dropdown,
    FormContainer,
    FormGroup,
    FullWidthInputGroup,
    Header,
    TableBody,
    TableData,
    TableHead,
    TableHeader,
    TableRow,
} from '../../elements/common';

interface ISelectOption {
    text: string;
    value: string;
}

interface IAuthSubsequentService extends ISubsequentService {
    authorization: IAuthorization;
}

interface ISubsequentServiceListProps {
    serviceEpisodeCategoryName?: string;
    serviceId: string;
}

export default function SubsequentServiceList(props: ISubsequentServiceListProps) {
    const [subsequentServices, setSubsequentServices] = useState<IAuthSubsequentService[]>([]);
    const [serviceOptions, setServiceOptions] = useState<ISelectOption[]>([]);
    const [serviceId, setServiceId] = useState<string | null>(null);

    assertHasAuthorization(ManagerAuthorization.servicesRead);

    useEffect(() => {
        loadSubsequentServices(props.serviceId);
    }, [props.serviceId]);

    useEffect(() => {
        loadPossibleServices(props.serviceId, props.serviceEpisodeCategoryName);
    }, [props.serviceId]);

    return (
        <>
            <Header content={translateKey('subsequentService.subsequentService')} />

            {hasOneAuthorization(ManagerAuthorization.serviceAddEdit) && (
                <FormContainer
                    onSubmit={() => {
                        return;
                    }}
                >
                    <FormGroup>
                        <FullWidthInputGroup>
                            <Dropdown
                                onChange={async (value) => setServiceId(value)}
                                placeholder={translateKey('common.add') + '...'}
                                selectOptions={serviceOptions}
                                value={serviceId}
                            />

                            <Button
                                color="primary"
                                onClick={async () => addService()}
                            >
                                {translateKey('common.addItem', translateKey('subsequentService.subsequentService'))}
                            </Button>
                        </FullWidthInputGroup>
                    </FormGroup>
                </FormContainer>
            )}

            <AdminTable>
                <TableHead>
                    <TableRow>
                        <TableHeader>{translateKey('service.service')}</TableHeader>
                        <TableHeader align="right"> </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {subsequentServices.map((item) => (
                        <TableRow key={item.id}>
                            <TableData>
                                {item.succeedingService && item.succeedingService.name && (
                                    <Link to={`/services/${item.succeedingServiceId}`}>
                                        {item.succeedingService.name}
                                    </Link>
                                )}
                            </TableData>
                            <TableData align="right">
                                {item.authorization.canDelete && <DeleteButton onClick={() => removeService(item)} />}
                            </TableData>
                        </TableRow>
                    ))}
                </TableBody>
            </AdminTable>
        </>
    );

    async function loadPossibleServices(id: string, serviceEpisodeCategoryName?: string) {
        let url = '/api/servicesList';
        if (serviceEpisodeCategoryName) {
            url += `?episodeCategory=${serviceEpisodeCategoryName}`;
        }

        const result = await fetchJson(url, 'GET');
        const options = result.json.items
            .filter((service: IService) => {
                // Filter service we are currently viewing
                if (service.id === id) {
                    return false;
                }

                // Filter items already present in list
                return !subsequentServices.some(
                    (subsequentService) => service.id === subsequentService.succeedingServiceId
                );
            })
            .map((service: IService) => {
                return { value: service.id, text: service.name };
            });

        setServiceOptions(options);
    }

    async function loadSubsequentServices(id: string) {
        const url = `/api/subsequentServices?serviceId=${id}&includeListAuthorizations=canAdd&includeItemAuthorizations=canDelete`;
        const result = await fetchJson(url, 'GET');
        if (result.json) {
            setSubsequentServices(result.json.items);
        }
    }

    async function addService() {
        if (!serviceId) {
            return;
        }
        const url = '/api/subsequentServices';

        const result = await fetchJson(url, 'POST', {
            succeedingServiceId: serviceId,
            previousServiceId: props.serviceId,
        });
        if (result.json) {
            setServiceId(null);
            loadSubsequentServices(props.serviceId);
            loadPossibleServices(props.serviceId, props.serviceEpisodeCategoryName);
        }
    }

    async function removeService(item: IAuthSubsequentService) {
        const url = `/api/subsequentServices/${item.id}`;
        const result = await fetchJson(url, 'DELETE');
        if (result.json) {
            await loadSubsequentServices(props.serviceId);
            await loadPossibleServices(props.serviceId, props.serviceEpisodeCategoryName);
        }
    }
}
